// App.js
import React, { useEffect, useState } from 'react';
import './App.css';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Security, useOktaAuth, LoginCallback } from '@okta/okta-react';
import { OktaAuth } from '@okta/okta-auth-js';
import oktaConfig from './oktaConfig';

// Components
import NavBar from './components/General/NavBar';
import QueryTool from './views/QueryTool';
import FileUpload from './views/FileUpload';
import DocumentRepository from './views/DocumentRepository';
import LogoutPage from './views/LogoutPage';

// Initialize OktaAuth instance
const oktaAuth = new OktaAuth({
  ...oktaConfig,
  tokenManager: {
    autoRenew: true,
    secure: true,
    storage: 'localStorage',
  },
});

// Redirect unauthenticated users
const ProtectedRoute = ({ element }) => {
  const { authState, oktaAuth } = useOktaAuth();

  useEffect(() => {
    if (authState && !authState.isAuthenticated) {
      oktaAuth.signInWithRedirect();
    }
  }, [authState, oktaAuth]);

  if (!authState || !authState.isAuthenticated) {
    return <div className="text-center mt-20 text-xl">Redirecting to login...</div>;
  }

  return element;
};

const AppRoutes = () => {
  const { authState, oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pageIndex, setPageIndex] = useState('p1');
  const [accessToken, setAccessToken] = useState(null);

  // Redirect from '/' to '/querytool' on first login
  useEffect(() => {
    if (authState?.isAuthenticated && location.pathname === '/') {
      navigate('/querytool', { replace: true });
    }
  }, [authState?.isAuthenticated, location.pathname, navigate]);

  // Track pageIndex based on current route
  useEffect(() => {
    const pathToIndex = {
      '/querytool': 'p1',
      '/fileupload': 'p2',
      '/docrepository': 'p3',
    };
    setPageIndex(pathToIndex[location.pathname] || 'p1');
  }, [location.pathname]);

  // Fetch and store access token once authenticated
  useEffect(() => {
    const fetchToken = async () => {
      if (authState?.isAuthenticated) {
        const token = await oktaAuth.tokenManager.get('accessToken');
        if (token?.accessToken) {
          setAccessToken(token.accessToken);
        }
      }
    };
    fetchToken();
  }, [authState, oktaAuth]);

  // Logout handler
  const handleLogout = async () => {
    // Call backend /expire endpoint before signing out
    await oktaAuth.signOut({
      postLogoutRedirectUri: window.location.origin + '/logout',
    });
  };

  return (
    <div style={{ height: '100vh' }}>
      <NavBar
        pageIndex={pageIndex}
        setPageIndex={(val) => {
          setPageIndex(val);
          const indexToPath = {
            p1: '/querytool',
            p2: '/fileupload',
            p3: '/docrepository',
          };
          navigate(indexToPath[val]);
        }}
        onLogout={handleLogout}
      />

      <div
        className="feedback-strip"
        role="link"
        aria-label="Feedback"
        title="Give feedback"
        onClick={() => {
          window.open('https://forms.office.com/Pages/ResponsePage.aspx?id=ywjFQzPFg0CBBXda4er3IT3WH7H3tNZErbnO5nEfmQNUM1pUN01CN0FZQzdDN1E2QUZTV0hDMU5QRSQlQCN0PWcu', '_blank', 'noopener,noreferrer');
        }}
      >
        <span className="feedback-inner">
          <span className="feedback-icon"><svg width="20px" height="20px" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill="#F0F8FF" d="M13.098 8H6.902c-.751 0-1.172.754-.708 1.268L9.292 12.7c.36.399 1.055.399 1.416 0l3.098-3.433C14.27 8.754 13.849 8 13.098 8Z"/></svg></span>
          <span className="feedback-text">Feedback</span>
        </span>
      </div>

      <Routes>
        <Route path="/login/callback" element={<LoginCallback />} />
        <Route path="/" element={<Navigate to="/querytool" replace />} />
        <Route
          path="/querytool"
          element={<ProtectedRoute element={<QueryTool accessToken={accessToken} />} />}
        />
        <Route
          path="/fileupload"
          element={<ProtectedRoute element={<FileUpload accessToken={accessToken} />} />}
        />
        <Route
          path="/docrepository"
          element={<ProtectedRoute element={<DocumentRepository accessToken={accessToken} />} />}
        />
        <Route path="/logout" element={<LogoutPage />} />
      </Routes>
    </div>
  );
};

// Wrap with Router and Okta Security context
const App = () => (
  <Router>
    <Security
      oktaAuth={oktaAuth}
      restoreOriginalUri={async (_oktaAuth, originalUri) => {
        window.history.replaceState({}, document.title, originalUri || '/querytool');
      }}
    >
      <AppRoutes />
    </Security>
  </Router>
);

export default App;
